import React, { ReactNode, useEffect, useMemo, useRef } from "react";
import ReactDOM from "react-dom";
import Button from "components/button";
import Icon from "components/icon";
import Typography from "components/typography";
import { useAppTranslation } from "locale/useAppTranslation";

interface ImportSidePanelProps {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
  onTemplateDownload?: () => void;
  onFileSelect: (file: File) => void;
  onFileRemove: () => void;
  selectedFile?: File | null;
  templateFileName?: string;
  title?: string;
  subtitle?: string;
  submitLabel?: string;
  submitDisabled?: boolean;
  submitLoading?: boolean;
  acceptedFileTypes?: string;
  widthClassName?: string;
  testIdPrefix?: string;
  children?: ReactNode;
}

const ImportSidePanel: React.FC<ImportSidePanelProps> = ({
  open,
  onClose,
  onSubmit,
  onTemplateDownload,
  onFileSelect,
  onFileRemove,
  selectedFile,
  templateFileName = "User Import Format.xlsx",
  title = "Import Data",
  subtitle = "Upload and import your data easily from a file",
  submitLabel = "Import",
  submitDisabled,
  submitLoading = false,
  acceptedFileTypes = ".xlsx,.xls,.csv",
  widthClassName = "max-w-[360px] md:max-w-[420px]",
  testIdPrefix = "import-side-panel",
  children,
}) => {
  const { t } = useAppTranslation("general");
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose, open]);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const selectedFileSize = useMemo(() => {
    if (!selectedFile) {
      return "";
    }

    const sizeInMb = selectedFile.size / (1024 * 1024);
    return `${sizeInMb.toFixed(1)} mb`;
  }, [selectedFile]);

  const fileIcon = useMemo(() => {
    if (!selectedFile) return "upload-icon";
    const extension = selectedFile.name.split(".").pop()?.toLowerCase();
    if (extension === "xlsx" || extension === "xls") return "xlsx-excel-thumb";
    return "document";
  }, [selectedFile]);

  if (typeof document === "undefined") {
    return null;
  }

  const templateIcon = useMemo(() => {
    const extension = templateFileName.split(".").pop()?.toLowerCase();
    if (extension === "xlsx" || extension === "xls") return "xlsx-excel-thumb";
    return "document";
  }, [templateFileName]);

  const isSubmitDisabled = submitDisabled ?? !selectedFile;

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const pickedFile = event.target.files?.[0];
    if (!pickedFile) {
      return;
    }

    onFileSelect(pickedFile);
    event.target.value = "";
  };

  const handleOpenFilePicker = () => {
    fileInputRef.current?.click();
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const droppedFile = event.dataTransfer.files?.[0];
    if (!droppedFile) {
      return;
    }

    onFileSelect(droppedFile);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  return ReactDOM.createPortal(
    <>
      <div
        className={`fixed inset-0 z-[1000] bg-black/30 transition-opacity duration-300 ${
          open ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
        onClick={onClose}
        aria-hidden="true"
        data-testid={`${testIdPrefix}-backdrop`}
      />

      <aside
        className={`fixed right-0 top-0 z-[1001] flex h-screen w-full ${widthClassName} transform flex-col overflow-hidden rounded-l-[24px] bg-white transition-transform duration-300 ease-out ${
          open ? "translate-x-0 shadow-[0_24px_80px_rgba(15,23,42,0.18)]" : "translate-x-full"
        }`}
        data-testid={testIdPrefix}
        aria-hidden={!open}
      >
        <div className="border-b border-border px-6 pb-5 pt-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <Typography variant="heading5" as="h2">
                {title}
              </Typography>
              <Typography variant="body" tone="muted" className="mt-2 max-w-[260px]">
                {subtitle}
              </Typography>
            </div>

            <Button
              type="button"
              variant="outline"
              color="tertiary"
              size="small"
              className="h-8 w-8 rounded-full border-0 p-0 text-text-secondary hover:bg-greyScale-90"
              onClick={onClose}
              aria-label="Close"
              data-testid={`${testIdPrefix}-close`}
            >
              <Icon name="close-2" size={18} />
            </Button>
          </div>
        </div>

        <div className="flex-1 px-6 py-6">
          <div className="space-y-5">
            {onTemplateDownload && (
              <Button
                type="button"
                variant="outline"
                color="tertiary"
                size="medium"
                isFullSize
                onClick={onTemplateDownload}
                className="flex justify-between items-center border-greyScale-200 bg-greyScale-50 rounded-xl px-4 py-3 hover:bg-greyScale-100 transition-colors"
                data-testid="btn-import-template"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <Icon 
                    name={templateIcon} 
                    size={24} 
                    className={templateIcon === "xlsx-excel-thumb" ? "" : "text-primary-600"} 
                    stroke={templateIcon === "xlsx-excel-thumb" ? "none" : undefined}
                  />
                  <Typography variant="body" className="truncate text-slate-500 font-medium">
                    {templateFileName}
                  </Typography>
                </div>
                <Icon name="import" size={20} className="text-primary-600 flex-shrink-0" />
              </Button>
            )}

            <div>
              <Typography variant="body" weight="semibold" className="mb-2">
                {t("import-upload-label")} <span className="text-error">*</span>
              </Typography>

              <div
                className="relative flex min-h-[145px] w-full flex-col items-center justify-center rounded-lg border border-dashed border-border bg-greyScale-95 px-4 py-4 text-center"
                onDrop={handleDrop}
                onDragOver={handleDragOver}
              >
                {selectedFile ? (
                  <>
                    <button
                      type="button"
                      onClick={onFileRemove}
                      className="absolute right-3 top-3 text-red-500 hover:text-red-600 transition-colors"
                      aria-label="Remove selected file"
                    >
                      <Icon name="close-circle" size={24} />
                    </button>
                    <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-xl bg-white shadow-sm border border-greyScale-100 transition-transform duration-200">
                      <Icon 
                        name={fileIcon} 
                        size={80} 
                        className={fileIcon === "xlsx-excel-thumb" ? "" : "text-primary-600"}
                        stroke={fileIcon === "xlsx-excel-thumb" ? "none" : undefined}
                      />
                    </div>
                    <Typography variant="body" className="mt-1 font-medium text-[#1E293B] max-w-[240px] truncate">
                      {selectedFile.name}
                    </Typography>
                    <Typography variant="bodySmall" className="mt-0.5 text-[#94A3B8]">
                      {selectedFileSize}
                    </Typography>
                  </>
                ) : (
                  <>
                    <Icon name="upload-icon" size={34} className="text-primary-600" />
                    <Typography variant="body" className="mt-2 font-medium">
                      {t("import-drag-drop")}
                    </Typography>
                    <Typography variant="bodyMedium" tone="muted" className="mt-1">
                      {t("import-drag-drop-hint")}
                    </Typography>
                  </>
                )}
              </div>

              {children && <div className="mt-4">{children}</div>}

              <input
                ref={fileInputRef}
                type="file"
                accept={acceptedFileTypes}
                className="hidden"
                onChange={handleFileInputChange}
              />

              <div className="mt-3">
                <Button
                  type="button"
                  variant="outline"
                  color="primary"
                  size="medium"
                  isFullSize
                  onClick={handleOpenFilePicker}
                  data-testid="btn-import-file"
                >
                  {t("import-choose-file")}
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 py-4">
          <Button
            type="button"
            color="primary"
            size="medium"
            isFullSize
            onClick={onSubmit}
            disabled={isSubmitDisabled || submitLoading}
            data-testid="btn-submit-import"
          >
            {submitLoading ? t("import-importing") : submitLabel}
          </Button>
        </div>
      </aside>
    </>,
    document.body,
  );
};

export default ImportSidePanel;